import { BaseTrack, TrackConfig, TrackData } from '../base/base-track';

export class NavigationTrack extends BaseTrack {
  private navigationElement?: HTMLElement;

  constructor(config: TrackConfig, data: TrackData) {
    super('navigation', config, data);
  }

  createHTML(): string {
    if (!this.hasRequiredData()) {
      return '';
    }

    return `
      <div style="display: flex; align-items: center;">
        <div style="width: ${this.config.labelWidth}px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 8px;">
          <div class="sequence-info">
            ${this.data.msaData.sequenceCount ? this.data.msaData.sequenceCount.toLocaleString() : 0} seed sequences
          </div>
          <div class="sequence-info" style="margin-top: 8px; margin-bottom: 4px;">
          ${this.config.sequenceLength.toLocaleString()} positions            
          </div>
          <div class="zoom-controls">
            <button class="zoom-btn zoom-out-btn" title="Zoom out (Ctrl + mouse wheel down)">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 1 0 7 7 7 7 0 0 0-7-7zm0 1a6 6 0 1 1-6 6 6 6 0 0 1 6-6z"/>
                <path d="M5 7h6v2H5z"/>
              </svg>
            </button>
            <button class="zoom-btn zoom-in-btn" title="Zoom in (Ctrl + mouse wheel up)">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 1a7 7 0 1 0 7 7 7 7 0 0 0-7-7zm0 1a6 6 0 1 1-6 6 6 6 0 0 1 6-6z"/>
                <path d="M8 5v2H6v2h2v2h2V9h2V7H10V5H8z"/>
              </svg>
            </button>
          </div>
        </div>
        <nightingale-navigation
          id="navigation-track"
          height="80"
          use-ctrl-to-zoom>
        </nightingale-navigation>
      </div>
    `;
  }

  setup(container: HTMLElement): void {
    this.element = container.querySelector('#navigation-track') as HTMLElement;
    
    if (!this.element) {
      console.error('Navigation track element not found');
      return;
    }

    this.setupNavigationTrack();
    this.setupZoomControls(container);
  }

  update(config?: Partial<TrackConfig>, data?: Partial<TrackData>): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    if (data) {
      this.data = { ...this.data, ...data };
    }
    
    if (this.element) {
      this.setupNavigationTrack();
    }
  }

  destroy(): void {
    this.element = undefined;
    this.navigationElement = undefined;
  }

  /**
   * Get the navigation element for zoom operations
   */
  getNavigationElement(): HTMLElement | undefined {
    return this.navigationElement;
  }

  /**
   * Zoom in
   */
  zoomIn(): void {
    if (this.navigationElement) {
      (this.navigationElement as any).zoomIn();
    }
  }

  /**
   * Zoom out
   */
  zoomOut(): void {
    if (this.navigationElement) {
      (this.navigationElement as any).zoomOut();
    }
  }

  private setupNavigationTrack(): void {
    if (!this.element) return;

    const nightingaleAttributes = this.getNightingaleAttributes();
    this.applyAttributes(this.element, {
      ...nightingaleAttributes,
      'height': '80',
      'use-ctrl-to-zoom': 'true'
    });

    this.navigationElement = this.element;
  }

  private setupZoomControls(container: HTMLElement): void {
    const zoomInBtn = container.querySelector('.zoom-in-btn');
    const zoomOutBtn = container.querySelector('.zoom-out-btn');
    
    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn());
    }
    
    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut());
    }
  }
}